import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BannerService } from '@app/core';
import { SHARED_MODULES } from '@app/shared';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import { interval, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { loadBanners, updateStorageBannersViewed } from '../../banner';

@Component({
  imports: [SHARED_MODULES],
  templateUrl: './modal-banners.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalBannersComponent implements OnInit, OnDestroy {
  protected banners$: Observable<any[]>;
  protected loading = signal(false);
  protected readonly current = signal(0);
  private readonly destroy$ = new Subject<void>();
  private readonly pause = signal(false);
  private readonly carousel = viewChild<ElementRef<HTMLDivElement>>('carousel');
  private readonly destroy = inject(DestroyRef);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly bannersService = inject(BannerService);
  private readonly _localStorageService = inject(LocalStorageService);

  public ngOnInit() {
    this.loading.set(true);
    this.banners$ = loadBanners(this.store, this.bannersService, this.destroy).pipe(
      tap(({ userId, banners }) => {
        this.destroy$.next();
        this.loadListeners(banners);
        updateStorageBannersViewed(this._localStorageService, userId, banners);
      }),
      map(({ banners }) => banners),
      tap(() => this.loading.set(false)),
    );
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onNavigate(index: number) {
    this.current.set(index);
    const children = this.carousel().nativeElement.children[0] as HTMLElement;
    this.carousel().nativeElement.scrollTo({
      left: (children.offsetWidth + 8) * index,
      behavior: 'smooth',
    });
  }

  public onLock() {
    this.pause.set(true);
  }

  public onUnlock() {
    this.pause.set(false);
  }

  private loadListeners(banners: any[]) {
    interval(6000)
      .pipe(
        takeUntil(this.destroy$),
        takeUntilDestroyed(this.destroy),
        filter(() => !(this.pause() || isEmpty(banners))),
      )
      .subscribe(() => this.onNavigate((this.current() + 1) % banners.length));
  }
}
